// frontend/src/components/scoring/HowToPlayLeaguesCarousel.tsx
// "How to Play - Leagues" explainer (owner-spec 2026-07-22): the FF-native
// lifecycle in five cards - Draft, Set Your Lineup, Manage Your Roster,
// Beat Your Friends, Keep Beating Your Friends - plus a Start a League CTA.
//
// The carousel/card shells deliberately MIRROR the locked HowItWorksCarousel
// (arrows, snap-scroll, dots, 420px CardShell) so the Daily and Leagues tabs
// feel like one system; that component is owner-locked, so the shell is
// duplicated here rather than extracted from it. Visuals are stylized mini-UI
// vignettes (same convention as the daily cards), never screenshots. The
// draft grid reuses the canonical position palette (positionColors.ts) so the
// explainer teaches the same color language as the real draft board.
import React, { useCallback, useEffect, useRef, useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Trophy, ArrowRightLeft, UserPlus, Zap } from 'lucide-react';
import { positionCellClasses, positionTextClasses } from '@/utils/positionColors';

const Carousel: React.FC<{ children: ReactNode[]; ariaLabel: string }> = ({ children, ariaLabel }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const updateActiveFromScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const containerCenter = container.scrollLeft + container.offsetWidth / 2;
    const cards = container.querySelectorAll<HTMLElement>('[data-card]');
    let closestIndex = 0;
    let closestDistance = Infinity;
    cards.forEach((card, idx) => {
      const cardCenter = card.offsetLeft + card.offsetWidth / 2;
      const distance = Math.abs(cardCenter - containerCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = idx;
      }
    });
    setActiveIndex(closestIndex);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener('scroll', updateActiveFromScroll, { passive: true });
    updateActiveFromScroll();
    return () => container.removeEventListener('scroll', updateActiveFromScroll);
  }, [updateActiveFromScroll, children.length]);

  const scrollToIndex = (idx: number) => {
    const container = containerRef.current;
    if (!container) return;
    const card = container.querySelector(`[data-index="${idx}"]`) as HTMLElement | null;
    card?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  };

  const goPrev = () => scrollToIndex(Math.max(0, activeIndex - 1));
  const goNext = () => scrollToIndex(Math.min(children.length - 1, activeIndex + 1));
  const atStart = activeIndex === 0;
  const atEnd = activeIndex === children.length - 1;

  return (
    <div className="relative">
      <button
        onClick={goPrev}
        disabled={atStart}
        aria-label="Previous card"
        className="hidden md:flex absolute left-[calc(50%-220px)] top-[180px] -translate-x-full -translate-y-1/2 z-10 items-center justify-center w-11 h-11 rounded-full bg-primary border-2 border-accent-yellow/80 text-accent-yellow shadow-lg hover:bg-primary hover:border-accent-yellow disabled:opacity-30 disabled:cursor-not-allowed transition-all"
      >
        <ChevronLeft size={22} />
      </button>
      <button
        onClick={goNext}
        disabled={atEnd}
        aria-label="Next card"
        className="hidden md:flex absolute right-[calc(50%-220px)] top-[180px] translate-x-full -translate-y-1/2 z-10 items-center justify-center w-11 h-11 rounded-full bg-primary border-2 border-accent-yellow/80 text-accent-yellow shadow-lg hover:bg-primary hover:border-accent-yellow disabled:opacity-30 disabled:cursor-not-allowed transition-all"
      >
        <ChevronRight size={22} />
      </button>

      <div
        ref={containerRef}
        role="region"
        aria-label={ariaLabel}
        className="flex gap-3 md:gap-5 overflow-x-auto snap-x snap-mandatory pb-3 -mx-4 px-[calc(50%-140px)] sm:px-[calc(50%-150px)] md:px-[calc(50%-160px)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {children.map((child, idx) => {
          const isActive = idx === activeIndex;
          return (
            <div
              key={idx}
              data-card
              data-index={idx}
              onClick={() => !isActive && scrollToIndex(idx)}
              className={`snap-center flex-shrink-0 w-[280px] sm:w-[300px] md:w-[320px] transition-all duration-300 ${
                isActive
                  ? 'md:opacity-100 md:scale-100'
                  : 'md:opacity-30 md:scale-95 md:cursor-pointer md:hover:opacity-60'
              }`}
            >
              {child}
            </div>
          );
        })}
      </div>

      <div className="flex justify-center gap-1.5 mt-2">
        {children.map((_, idx) => (
          <button
            key={idx}
            onClick={() => scrollToIndex(idx)}
            aria-label={`Go to card ${idx + 1}`}
            className={`h-2 rounded-full transition-all duration-200 ${
              idx === activeIndex ? 'w-6 bg-accent-yellow' : 'w-2 bg-gray-600 hover:bg-gray-500'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

const CardShell: React.FC<{ accentColor?: string; children: ReactNode }> = ({ accentColor = '#ffc42c', children }) => (
  <div
    className="bg-surface-card rounded-xl border border-gray-700/60 h-[420px] flex flex-col p-5 relative overflow-hidden"
    style={{ borderTopWidth: '3px', borderTopColor: accentColor }}
  >
    {children}
  </div>
);

const StepHeader: React.FC<{ step: number; title: string }> = ({ step, title }) => (
  <div className="text-center mb-3">
    <p className="text-badge font-bold uppercase tracking-widest text-gray-400 mb-0.5">Step {step}</p>
    <p className="text-base font-heading font-bold text-white leading-tight">{title}</p>
  </div>
);

/* ── Step 1: Draft ─────────────────────────────────────────── */

// 4 league members x 4 rounds of the snake. Positions drive the cell colors
// (canonical palette) and are VALIDATED against player.primary_position in
// prod (2026-07-22) - do not guess positions when editing this mock.
const DRAFT_TEAMS = ['You', 'Diamond Dogs', 'Tony Perkis', 'Three Comma Club'];
const DRAFT_GRID: ({ pos: string; name: string } | 'clock' | null)[][] = [
  [
    { pos: 'FW', name: 'Mbappé' },
    { pos: 'FW', name: 'Haaland' },
    { pos: 'MF', name: 'Bruno' },
    { pos: 'FW', name: 'Yamal' },
  ],
  [
    { pos: 'MF', name: 'Pedri' },
    { pos: 'FW', name: 'Vini Jr.' },
    { pos: 'MF', name: 'Palmer' },
    { pos: 'DF', name: 'Gvardiol' },
  ],
  // Snake order: "You" pick FIRST in round 3, so the rest of the round is
  // still empty while you're on the clock (FF-natives will check this).
  ['clock', null, null, null],
  [null, null, null, null],
];

const DraftGridCard: React.FC = () => (
  <CardShell>
    <StepHeader step={1} title="Draft Your Squad" />
    <div className="flex-grow">
      <div className="grid grid-cols-4 gap-1 mb-1">
        {DRAFT_TEAMS.map((team) => (
          <div
            key={team}
            className={`text-micro font-bold text-center truncate ${
              team === 'You' ? 'text-accent-yellow' : 'text-gray-400'
            }`}
          >
            {team}
          </div>
        ))}
      </div>
      <div className="space-y-1">
        {DRAFT_GRID.map((round, rIdx) => (
          <div key={rIdx} className="grid grid-cols-4 gap-1">
            {round.map((cell, cIdx) => {
              if (cell === 'clock') {
                return (
                  <div
                    key={cIdx}
                    className="rounded border-2 border-accent-yellow bg-accent-yellow/10 px-1 py-1.5 text-center animate-pulse"
                  >
                    <p className="text-micro font-bold text-accent-yellow leading-tight">ON THE</p>
                    <p className="text-micro font-bold text-accent-yellow leading-tight">CLOCK</p>
                  </div>
                );
              }
              if (cell === null) {
                // py matches the filled cells' two-line height so empty
                // rounds don't render visibly shorter than drafted ones.
                return <div key={cIdx} className="rounded border border-gray-700/40 bg-primary/40 py-[15px]" />;
              }
              return (
                <div key={cIdx} className={`rounded border px-1 py-1 text-center ${positionCellClasses(cell.pos)}`}>
                  <p className="text-micro font-bold leading-tight">{cell.pos}</p>
                  <p className="text-micro text-gray-200 leading-tight truncate">{cell.name}</p>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
    <p className="text-caption font-bold text-white text-center mt-3 leading-snug">
      Live snake draft with your league. <span className="text-accent-yellow">15 rounds</span> to build your squad.
    </p>
  </CardShell>
);

/* ── Step 2: Set Your Lineup ───────────────────────────────── */

// Positions validated against prod (Yamal/Vinícius = FW, Szoboszlai = MF).
// Points sum to 143.0 - the same roster and total reappear on the Matchup
// card so the two vignettes tell one continuous story.
const MY_TEAM_STARTERS = [
  { pos: 'FW', name: 'K. Mbappé', pts: '24.5' },
  { pos: 'FW', name: 'Vini Jr.', pts: '14.0' },
  { pos: 'MF', name: 'Bruno Fernandes', pts: '28.5' },
  { pos: 'MF', name: 'Pedri', pts: '16.0' },
  { pos: 'DF', name: 'V. van Dijk', pts: '11.5' },
  { pos: 'DF', name: 'P. Porro', pts: '13.0' },
  { pos: 'FLEX', name: 'Lamine Yamal', pts: '22.0' },
  { pos: 'FLEX', name: 'D. Szoboszlai', pts: '13.5' },
];

// FLEX has no entry in the canonical palette - neutral gray, matching how
// the real roster surfaces render the FLEX slot label.
const posLabelClasses = (pos: string): string =>
  pos === 'FLEX' ? 'text-gray-300' : positionTextClasses(pos);

const RosterRow: React.FC<{ pos: string; name: string; right?: string }> = ({ pos, name, right }) => (
  <div className="flex items-center rounded px-2 py-1 border bg-primary/60 border-accent-yellow/15">
    <span className={`text-micro font-bold w-10 text-center flex-shrink-0 ${posLabelClasses(pos)}`}>
      {pos}
    </span>
    <span className="flex-grow text-caption font-medium truncate text-gray-100">{name}</span>
    {right && <span className="text-caption font-bold flex-shrink-0 ml-2 text-accent-yellow">{right}</span>}
  </div>
);

const MyTeamCard: React.FC = () => (
  <CardShell accentColor="#78b4e6">
    <StepHeader step={2} title="Set Your Lineup" />
    <div className="space-y-1 flex-grow">
      {MY_TEAM_STARTERS.map((p, i) => (
        <RosterRow key={i} pos={p.pos} name={p.name} right={p.pts} />
      ))}
    </div>
    <p className="text-caption font-bold text-white text-center mt-2 leading-snug">
      Start 8: <span className="text-accent-yellow">2 FW · 2 MF · 2 DF · 2 FLEX</span>. Bench: 7.
    </p>
  </CardShell>
);

/* ── Step 3: Manage Your Roster ────────────────────────────── */

// All names/positions validated against prod (Mikautadze FW / Watkins FW is a
// like-for-like waiver swap; Puado FW is a plausible free-agent add).
const ManageRosterCard: React.FC = () => (
  <CardShell>
    <StepHeader step={3} title="Manage Your Roster" />
    <div className="flex-grow space-y-2">
      <div className="rounded-lg border border-gray-700/60 bg-primary/60 p-2.5">
        <p className="text-micro font-bold uppercase tracking-widest text-accent-yellow mb-1.5 flex items-center gap-1.5">
          <Zap size={12} /> Free Agents
        </p>
        <div className="flex items-center justify-between">
          <p className="text-caption text-gray-100 truncate">
            <span className="text-green-400 font-bold">+</span> Javi Puado{' '}
            <span className={`text-micro font-bold ${posLabelClasses('FW')}`}>FW</span>
          </p>
          <span className="flex-shrink-0 ml-2 px-2.5 py-0.5 rounded bg-green-600/20 text-green-400 text-micro font-bold uppercase tracking-wider ring-1 ring-green-500/30">
            Add Now
          </span>
        </div>
      </div>
      <div className="rounded-lg border border-gray-700/60 bg-primary/60 p-2.5">
        <p className="text-micro font-bold uppercase tracking-widest text-accent-yellow mb-1.5 flex items-center gap-1.5">
          <ArrowRightLeft size={12} /> Trade Offer
        </p>
        <p className="text-caption text-gray-100 mb-0.5">
          You get <span className="font-bold text-white">E. Haaland</span>
        </p>
        <p className="text-caption text-gray-400 mb-1.5">
          You send <span className="font-bold">Raphinha</span> + <span className="font-bold">P. Porro</span>
        </p>
        <div className="flex gap-2">
          <span className="flex-1 py-1 rounded bg-green-600/20 text-green-400 text-center text-micro font-bold uppercase tracking-wider ring-1 ring-green-500/30">
            Accept
          </span>
          <span className="flex-1 py-1 rounded bg-red-600/20 text-red-400 text-center text-micro font-bold uppercase tracking-wider ring-1 ring-red-500/30">
            Decline
          </span>
        </div>
      </div>
      <div className="rounded-lg border border-gray-700/60 bg-primary/60 p-2.5">
        <p className="text-micro font-bold uppercase tracking-widest text-accent-yellow mb-1.5 flex items-center gap-1.5">
          <UserPlus size={12} /> Waiver Claim
        </p>
        <div className="flex items-center justify-between">
          <p className="text-caption text-gray-100 truncate">
            <span className="text-green-400 font-bold">+</span> G. Mikautadze{' '}
            <span className="text-gray-400">
              <span className="text-red-400 font-bold">-</span> O. Watkins
            </span>
          </p>
          <p className="text-caption font-bold text-accent-yellow flex-shrink-0 ml-2">
            $23 <span className="text-micro font-medium text-gray-500">· Wed</span>
          </p>
        </div>
      </div>
    </div>
    <p className="text-caption font-bold text-white text-center mt-3 leading-snug">
      Trades, <span className="text-accent-yellow">Free Agents/Waivers</span>, and FAAB.
    </p>
  </CardShell>
);

/* ── Step 4: Beat Your Friends ─────────────────────────────── */

// Full 8-slot rosters, positions validated against prod. Each side's points
// sum EXACTLY to the displayed total (143.0 / 128.5) - FF-natives add these
// up. "You" mirror the Set Your Lineup card (same roster, same 143.0).
const MATCHUP_YOU = [
  { pos: 'FW', name: 'Mbappé', pts: '24.5' },
  { pos: 'FW', name: 'Vini Jr.', pts: '14.0' },
  { pos: 'MF', name: 'Bruno', pts: '28.5' },
  { pos: 'MF', name: 'Pedri', pts: '16.0' },
  { pos: 'DF', name: 'Van Dijk', pts: '11.5' },
  { pos: 'DF', name: 'Porro', pts: '13.0' },
  { pos: 'FLEX', name: 'Yamal', pts: '22.0' },
  { pos: 'FLEX', name: 'Szoboszlai', pts: '13.5' },
];
const MATCHUP_THEM = [
  { pos: 'FW', name: 'Haaland', pts: '18.0' },
  { pos: 'FW', name: 'Saka', pts: '16.5' },
  { pos: 'MF', name: 'Palmer', pts: '21.0' },
  { pos: 'MF', name: 'Rice', pts: '16.0' },
  { pos: 'DF', name: 'Gabriel', pts: '9.0' },
  { pos: 'DF', name: 'Gvardiol', pts: '10.5' },
  { pos: 'FLEX', name: 'Raphinha', pts: '25.0' },
  { pos: 'FLEX', name: 'Cunha', pts: '12.5' },
];

const MatchupCard: React.FC = () => (
  <CardShell accentColor="#e24b4a">
    <StepHeader step={4} title="Beat Your Friends" />
    <div className="text-center mb-2">
      <p className="text-badge font-bold uppercase tracking-widest text-gray-400 mb-1 flex items-center justify-center gap-1.5">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400" />
        </span>
        Week 7 · Live
      </p>
    </div>
    <div className="grid grid-cols-2 gap-2 flex-grow">
      <div className="rounded-lg border border-accent-yellow/60 bg-accent-yellow/10 p-1.5">
        <p className="text-micro font-bold text-accent-yellow truncate px-0.5">You</p>
        <p className="text-xl font-heading font-bold text-accent-yellow leading-tight mb-1.5 px-0.5">143.0</p>
        <div className="space-y-1">
          {MATCHUP_YOU.map((p, i) => (
            <div key={i} className="flex items-center rounded border bg-primary/60 border-accent-yellow/15 px-1.5 py-0.5">
              <span className={`text-micro font-bold w-7 flex-shrink-0 ${posLabelClasses(p.pos)}`}>{p.pos}</span>
              <span className="flex-grow text-micro font-medium text-gray-100 truncate">{p.name}</span>
              <span className="text-micro font-bold text-accent-yellow flex-shrink-0 ml-1">{p.pts}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-lg border border-gray-700/60 bg-primary/40 p-1.5">
        <p className="text-micro font-bold text-gray-300 truncate px-0.5">Diamond Dogs</p>
        <p className="text-xl font-heading font-bold text-gray-300 leading-tight mb-1.5 px-0.5">128.5</p>
        <div className="space-y-1">
          {MATCHUP_THEM.map((p, i) => (
            <div key={i} className="flex items-center rounded border bg-primary/60 border-gray-700/50 px-1.5 py-0.5">
              <span className={`text-micro font-bold w-7 flex-shrink-0 ${posLabelClasses(p.pos)}`}>{p.pos}</span>
              <span className="flex-grow text-micro font-medium text-gray-300 truncate">{p.name}</span>
              <span className="text-micro font-bold text-gray-400 flex-shrink-0 ml-1">{p.pts}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
    <p className="text-caption font-bold text-white text-center mt-3 leading-snug">
      Head to head against your league, <span className="text-accent-yellow">every week</span>.
    </p>
  </CardShell>
);

/* ── Step 5: Keep Beating Your Friends ─────────────────────── */

const STANDINGS = [
  { rank: 1, team: 'You', record: '8-1', pf: '1,204.5', highlight: true },
  { rank: 2, team: 'Diamond Dogs', record: '6-3', pf: '1,141.0', highlight: false },
  { rank: 3, team: 'Tony Perkis', record: '5-4', pf: '1,098.5', highlight: false },
  { rank: 4, team: 'Three Comma Club', record: '5-4', pf: '1,067.0', highlight: false },
  { rank: 5, team: 'The Revolting Blob', record: '4-5', pf: '1,010.5', highlight: false },
  { rank: 6, team: 'War Daddy', record: '2-7', pf: '911.0', highlight: false },
];

const StandingsCard: React.FC = () => (
  <CardShell>
    <StepHeader step={5} title="Keep Beating Your Friends" />
    <div className="flex items-center px-2 mb-1">
      <span className="w-5 flex-shrink-0" />
      <span className="flex-grow text-micro font-bold uppercase tracking-widest text-gray-500">Team</span>
      <span className="w-10 text-right text-micro font-bold uppercase tracking-widest text-gray-500">W-L</span>
      <span className="w-14 text-right text-micro font-bold uppercase tracking-widest text-gray-500">PF</span>
    </div>
    <div className="space-y-1 flex-grow">
      {STANDINGS.map((row) => (
        <div
          key={row.rank}
          className={`flex items-center rounded px-2 py-1.5 border ${
            row.highlight ? 'bg-accent-yellow/15 border-accent-yellow/60' : 'bg-primary/60 border-accent-yellow/10'
          }`}
        >
          <span className={`text-micro font-bold w-5 flex-shrink-0 ${row.highlight ? 'text-accent-yellow' : 'text-gray-400'}`}>
            {row.rank}
          </span>
          <span className={`flex-grow text-caption truncate ${row.highlight ? 'text-accent-yellow font-bold' : 'text-gray-100'}`}>
            {row.team}
            {row.highlight && <Trophy size={12} className="inline ml-1.5 -mt-0.5 text-accent-yellow" />}
          </span>
          <span className={`w-10 text-right text-caption font-bold ${row.highlight ? 'text-accent-yellow' : 'text-gray-300'}`}>
            {row.record}
          </span>
          <span className={`w-14 text-right text-caption ${row.highlight ? 'text-accent-yellow font-bold' : 'text-gray-400'}`}>
            {row.pf}
          </span>
        </div>
      ))}
    </div>
    <p className="text-caption font-bold text-white text-center mt-3 leading-snug">
      Climb the standings. <span className="text-accent-yellow">Take the title.</span>
    </p>
  </CardShell>
);

/* ── CTA ───────────────────────────────────────────────────── */

const CtaCard: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    onClick={onClick}
    className="w-full h-[420px] bg-accent-yellow rounded-xl border-2 border-accent-yellow flex flex-col items-center justify-center p-5 hover:bg-accent-yellow/90 active:bg-accent-yellow/90 transition-colors"
  >
    <Trophy size={48} className="text-primary mb-4" />
    <p className="text-xl font-heading font-bold uppercase tracking-wider text-primary">Start a League</p>
  </button>
);

/* ── Main component ────────────────────────────────────────── */

interface HowToPlayLeaguesCarouselProps {
  onCtaClick?: () => void;
}

const HowToPlayLeaguesCarousel: React.FC<HowToPlayLeaguesCarouselProps> = ({ onCtaClick }) => {
  const navigate = useNavigate();

  const cards = [
    <DraftGridCard key="draft" />,
    <MyTeamCard key="lineup" />,
    <ManageRosterCard key="manage" />,
    <MatchupCard key="matchup" />,
    <StandingsCard key="standings" />,
    <CtaCard key="cta" onClick={onCtaClick ?? (() => navigate('/season-long/dashboard'))} />,
  ];

  return (
    <section>
      <div className="flex items-center gap-3 mb-4 md:mb-6 px-4">
        <div className="flex-1 h-px bg-gray-700/50" />
        <span className="text-lg md:text-2xl font-heading font-bold uppercase tracking-wider text-accent-yellow whitespace-nowrap">
          How to Play
        </span>
        <div className="flex-1 h-px bg-gray-700/50" />
      </div>
      <Carousel ariaLabel="How to Play league steps">{cards}</Carousel>
    </section>
  );
};

export default HowToPlayLeaguesCarousel;
